import { legacyCreateColumnHelper as createColumnHelper } from '@tanstack/react-table/legacy'
import { Plus } from 'lucide-react'
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { DataTable } from '@/components/data-table/DataTable'
import { StatCard } from '@/components/stat/StatCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useQueryState } from '@/hooks/use-query-state'
import { useTablePreferences } from '@/hooks/use-table-preferences'
import { formatCubeDateTime, formatInteger, formatText } from '@/lib/format'
import { messages } from '@/lib/messages'
import { HAREKET_MEMBERS, HR_VIEWS, type HareketRow } from '@/services/cube/hr'
import { HrFilterBar } from '@/features/hr/list'
import { useHrCubeFilters, useHrList } from '@/features/hr/use-hr-list'
import {
  ActionStatusBadge,
  DetailGrid,
  PersonCell,
  RecordActions,
  RecordHistory,
  RecordStatusBadge,
  SubRowLayout,
} from '@/features/hr/parts'
import { useLatestActions } from '@/features/hr/hr-actions'
import { hareketActions } from '@/features/hr/action-specs'
import { HrPageHeader } from '@/features/hr/HrPageHeader'
import { RequestFormSheet, type RequestFormTarget } from '@/features/hr/RequestForm'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr
const a = t.attendance
const VIEW = HR_VIEWS.hareket
const column = createColumnHelper<HareketRow>()

const STATUS_OPTIONS = ['bekliyor', 'hatali', 'aktarildi', 'silindi', 'yoksayildi', 'elle_girildi']
const CATEGORY_OPTIONS = [
  'firma_eslesmesi',
  'donem_kodu',
  'gecersiz_tip',
  'izin_cakismasi',
  'oturum',
  'dia_hatasi',
]

export function CategoryBadge({ category }: { category: string | null }) {
  if (!category) return null
  return (
    <Badge tone={category === 'izin_cakismasi' ? 'warning' : 'danger'} size="sm">
      {t.category[category] ?? category}
    </Badge>
  )
}

/** Panelden onaylı talep ile DİA'ya eklenmiş hareket (Passgage'de yok). */
export function PanelBadge({ row }: { row: Pick<HareketRow, 'kaynak'> }) {
  if (row.kaynak !== 'panel') return null
  return (
    <Badge tone="info" size="sm">
      {a.panelBadge}
    </Badge>
  )
}

/** Talep hedefi: hareketin kendisi (düzenleme ya da silme). */
function movementTarget(row: HareketRow, kind: RequestFormTarget['kind']): RequestFormTarget {
  return {
    kind,
    sicilno: row.sicilno ?? '',
    name: row.adisoyadi,
    hareketId: row.id,
    girisCikis: row.tip === 'check_out' ? 'Ç' : 'G',
    wallClock: row.zaman,
  }
}

/** Panelden eklenen hareketin aksiyonları: Passgage kaydı olmadığı için yalnız talep ile. */
function PanelMovementActions({
  row,
  onRequest,
}: {
  row: HareketRow
  onRequest: (target: RequestFormTarget) => void
}) {
  const canRequest = useAuthStore((state) => state.hasPermission('hr.request:create'))
  return (
    <>
      <p className="text-text-secondary text-xs">{a.panelHint}</p>
      {canRequest && row.sicilno && row.durum === 'aktarildi' && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onRequest(movementTarget(row, 'giris_cikis_duzenle'))}
          >
            {a.edit}
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => onRequest(movementTarget(row, 'giris_cikis_sil'))}
          >
            {a.delete}
          </Button>
        </div>
      )}
    </>
  )
}

function MovementDetail({
  row,
  onEditRequest,
}: {
  row: HareketRow
  onEditRequest: (target: RequestFormTarget) => void
}) {
  const canRequest = useAuthStore((state) => state.hasPermission('hr.request:create'))
  const panel = row.kaynak === 'panel'
  return (
    <SubRowLayout
      detail={
        <>
          <DetailGrid
            items={[
              [t.detail.fields.zaman, formatCubeDateTime(row.zaman)],
              [
                t.detail.fields.tip,
                <span key="tip" className="flex flex-wrap items-center gap-2">
                  {t.girisCikis[row.tip ?? ''] ?? formatText(row.tip)}
                  <PanelBadge row={row} />
                </span>,
              ],
              [t.detail.fields.sicil, formatText(row.sicilno)],
              [t.detail.fields.firma, formatText(row.firma)],
              [t.detail.fields.sube, formatText(row.sube)],
              [t.filters.status, <RecordStatusBadge key="s" status={row.durum} />],
              [t.filters.category, <CategoryBadge key="c" category={row.hataKategori} />],
              [t.detail.error, formatText(row.hataMetni)],
              [t.detail.attempts, formatInteger(row.denemeSayisi)],
              [t.detail.lastAttempt, formatCubeDateTime(row.sonDeneme)],
              [t.detail.diaKey, formatText(row.diaKey)],
              ...(row.karar
                ? ([
                    [
                      t.detail.decision,
                      [
                        t.recordStatus[row.karar === 'yoksay' ? 'yoksayildi' : 'elle_girildi'],
                        row.kararAciklama,
                        row.kararKullanici && t.detail.decisionBy(row.kararKullanici),
                      ]
                        .filter(Boolean)
                        .join(' — '),
                    ],
                  ] as [string, string][])
                : []),
              ...(row.kapsayanIzinId
                ? ([
                    [
                      t.detail.coveringLeave,
                      <Link
                        key="leave"
                        to={`/ik/izinler?id=${encodeURIComponent(row.kapsayanIzinId)}`}
                        className="text-brand underline-offset-2 hover:underline"
                      >
                        {t.detail.openLeave}
                      </Link>,
                    ],
                  ] as [string, ReactNode][])
                : []),
            ]}
          />
          {panel ? (
            <PanelMovementActions row={row} onRequest={onEditRequest} />
          ) : (
            <div className="flex flex-wrap gap-2">
              <RecordActions kind="hareket" id={row.id} actions={hareketActions(row)} />
              {/* Anahtarsız hatalı hareket de düzenlenir: onaylanınca düzeltilmiş haliyle DİA'ya eklenir. */}
              {canRequest && row.sicilno && (row.diaKey || row.durum === 'hatali') && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onEditRequest(movementTarget(row, 'giris_cikis_duzenle'))}
                >
                  {t.detail.newEditRequest}
                </Button>
              )}
            </div>
          )}
        </>
      }
      history={<RecordHistory kind="hareket" id={row.id} />}
    />
  )
}

export function AttendancePage() {
  const query = useQueryState()
  // Talepten / geçmişten gelen bağlantı (`?id=`) hareketi açık getirir.
  const focusId = query.get('id')
  const filters = useHrCubeFilters(VIEW, {
    statusMember: 'durum',
    categoryMember: 'hataKategori',
    sourceMember: 'kaynak',
    dateMember: 'zaman',
    extra: focusId ? [{ member: `${VIEW}.id`, operator: 'equals', values: [focusId] }] : [],
  })
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    focusId ? { [focusId]: true } : {},
  )
  const canCreate = useAuthStore((state) => state.hasPermission('hr.request:create'))
  const list = useHrList<HareketRow>({
    view: VIEW,
    members: HAREKET_MEMBERS,
    rowKey: 'id',
    filters,
    defaultSort: [{ id: 'zaman', desc: true }],
    measures: ['count', 'hataliSayisi'],
  })
  const actions = useLatestActions(
    'hareket',
    list.rows.map((row) => row.id),
    VIEW,
  )
  const preferences = useTablePreferences('ikhareket', {})
  const [requestTarget, setRequestTarget] = useState<RequestFormTarget | null>(null)
  // Sabit kimlik: satır gövdesi (`TableRows`, memo) her sayfa render'ında yeniden çizilmesin.
  const renderSubRow = useCallback(
    (row: HareketRow) => <MovementDetail row={row} onEditRequest={setRequestTarget} />,
    [],
  )

  const columns = useMemo(
    () => [
      column.accessor((row) => row.adisoyadi, {
        id: 'adisoyadi',
        header: a.columns.ad,
        size: 220,
        cell: (info) => <PersonCell name={info.getValue()} />,
      }),
      column.accessor((row) => row.sicilno, {
        id: 'sicilno',
        header: a.columns.sicil,
        size: 90,
        cell: (info) => formatText(info.getValue()),
      }),
      column.accessor((row) => t.girisCikis[row.tip ?? ''] ?? row.tip, {
        id: 'tip',
        header: a.columns.tip,
        size: 150,
        cell: (info) => (
          <span className="flex items-center gap-1.5">
            {info.getValue()}
            <PanelBadge row={info.row.original} />
          </span>
        ),
      }),
      column.accessor((row) => row.zaman, {
        id: 'zaman',
        header: a.columns.zaman,
        size: 160,
        cell: (info) => formatCubeDateTime(info.getValue()),
      }),
      column.accessor((row) => row.firma, {
        id: 'firma',
        header: a.columns.firma,
        cell: (info) => formatText(info.getValue()),
      }),
      column.accessor((row) => row.sube, {
        id: 'sube',
        header: a.columns.sube,
        cell: (info) => formatText(info.getValue()),
      }),
      column.accessor((row) => row.durum, {
        id: 'durum',
        header: a.columns.durum,
        size: 160,
        cell: (info) => <RecordStatusBadge status={info.getValue()} />,
        meta: { exportValue: (row: HareketRow) => t.recordStatus[row.durum] ?? row.durum },
      }),
      column.accessor((row) => row.hataKategori, {
        id: 'hataKategori',
        header: a.columns.kategori,
        size: 170,
        cell: (info) => <CategoryBadge category={info.getValue()} />,
        meta: {
          exportValue: (row: HareketRow) => t.category[row.hataKategori ?? ''] ?? '',
        },
      }),
      column.accessor((row) => row.denemeSayisi, {
        id: 'denemeSayisi',
        header: a.columns.deneme,
        size: 80,
        cell: (info) => formatInteger(info.getValue()),
        meta: { numeric: true },
      }),
      column.display({
        id: 'aksiyon',
        header: a.columns.aksiyon,
        size: 120,
        cell: (info) => <ActionStatusBadge action={actions.get(info.row.original.id)} />,
        meta: { exportable: false },
      }),
    ],
    [actions],
  )

  return (
    <div className="flex flex-col gap-4">
      <HrPageHeader
        title={a.title}
        description={a.description}
        actions={
          canCreate && (
            <Button size="sm" onClick={() => setRequestTarget({ kind: 'giris_cikis_ekle' })}>
              <Plus /> {t.approvals.manualAdd}
            </Button>
          )
        }
      />
      <HrFilterBar
        view={VIEW}
        idPrefix="hr-hareket"
        extraKeys={['id']}
        withDates
        optionFilters={[
          {
            key: 'durum',
            label: t.filters.status,
            options: STATUS_OPTIONS.map((value) => ({ value, label: t.recordStatus[value] })),
          },
          {
            key: 'kategori',
            label: t.filters.category,
            options: CATEGORY_OPTIONS.map((value) => ({ value, label: t.category[value] })),
          },
          {
            key: 'kaynak',
            label: t.filters.source,
            options: ['passgage', 'panel'].map((value) => ({ value, label: t.source[value] })),
          },
        ]}
      />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label={a.total} value={formatInteger(list.measures.count)} busy={list.busy} />
        <StatCard
          label={a.errors}
          value={formatInteger(list.measures.hataliSayisi)}
          busy={list.busy}
        />
      </div>
      <DataTable
        tableId="ikhareket"
        title={a.title}
        columns={columns}
        data={list.rows}
        total={list.total}
        getRowId={(row) => row.id}
        sorting={list.sorting}
        onSortingChange={list.onSortingChange}
        pagination={list.pagination}
        onPaginationChange={list.setPagination}
        isLoading={list.isLoading}
        busy={list.busy}
        isError={list.isError}
        error={list.error}
        onRetry={list.refetch}
        layout={preferences.layout}
        onLayoutChange={preferences.save}
        onLayoutReset={preferences.reset}
        expanded={expanded}
        onExpandedChange={setExpanded}
        renderSubRow={renderSubRow}
      />
      <RequestFormSheet target={requestTarget} onClose={() => setRequestTarget(null)} />
    </div>
  )
}
